import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FilterGroup, FilterOp, FilterRule, GridColumnDef, GridDataType, LookupOption } from './grid-types';
import { GridApiService } from './services/grid-api.service';


type GridFilterKind = 'string' | 'number' | 'date' | 'bool'|'datetime';
export const FILTER_OP_LABELS: Record<FilterOp, string> = {
  Eq: 'Equals',
  Neq: 'Not Equal',
  Lt: 'Less Than',
  Lte: 'Less Than or Equal To',
  Gt: 'Greater Than',
  Gte: 'Greater Than or Equal To',
  Contains: 'Contains',
  StartsWith: 'Starts With',
  In: 'In List',
  IsNull: 'Is Empty',
  IsNotNull: 'Is Not Empty',
};

const KIND_BY_DATATYPE: Record<GridDataType, GridFilterKind> = {
  String: 'string',
  Int: 'number',
  Double: 'number',
  Date: 'date',
  Bool: 'bool',
  DateTime: 'datetime'
};

const OPS_BY_KIND: Record<GridFilterKind, FilterOp[]> = {
  string: ['Eq', 'Neq', 'Contains', 'StartsWith', 'In', 'IsNull', 'IsNotNull'],
  number: ['Eq', 'Neq', 'Gt', 'Gte', 'Lt', 'Lte', 'In', 'IsNull', 'IsNotNull'],
  date: ['Eq', 'Neq', 'Gt', 'Gte', 'Lt', 'Lte', 'IsNull', 'IsNotNull'],
  bool: ['Eq', 'Neq', 'IsNull', 'IsNotNull'],
  datetime: ['Eq', 'Neq', 'Gt', 'Gte', 'Lt', 'Lte', 'IsNull', 'IsNotNull']
};

@Component({
  selector: 'wc-filter-builder',
  standalone: false,
  styleUrls: ['./grid-theme.css'],
  template: `
  <div class="wcg-panel">
  <div class="wcg-panel__header">
    <strong>Filters</strong>
    <span class="wcg-panel__spacer"></span>
    <button class="wcg-btn wcg-btn--ghost wcg-btn--xs" (click)="addRule(model)">+ Add Filter</button>
    <button class="wcg-btn wcg-btn--primary wcg-btn--xs" (click)="applyFilters()">Apply</button>
    <button class="wcg-btn wcg-btn--danger wcg-btn--xs" (click)="clear()">Clear</button>
  </div>

  <div class="wcg-panel__body">
    <div *ngIf="model.rules.length === 0 && (!model.groups || model.groups.length === 0)"
         class="wcg-empty-hint">
      No filters applied — click <strong>+ Add Filter</strong> to narrow the results.
    </div>
    <ng-container *ngTemplateOutlet="groupTpl; context:{ g: model, depth: 0 }"></ng-container>
  </div>
</div>

<ng-template #groupTpl let-g="g" let-depth="depth">
  <div class="wcg-filter-group" [style.marginLeft.px]="depth * 14">
    <div class="wcg-filter-group__header"
         *ngIf="g.rules.length + (g.groups?.length ?? 0) > 1 || depth > 0">
      <label class="wcg-filter-group__label">Match:</label>
      <select class="wcg__select" [(ngModel)]="g.logic" (change)="emit()">
        <option value="AND">All conditions</option>
        <option value="OR">Any condition</option>
      </select>

      <span class="wcg-filter-group__spacer"></span>

      <button class="wcg-btn wcg-btn--ghost wcg-btn--xs" (click)="addRule(g)">+ Add Filter</button>
      <button class="wcg-btn wcg-btn--ghost wcg-btn--xs" (click)="addGroup(g)">+ Group</button>
      <button class="wcg-btn wcg-btn--danger wcg-btn--xs" *ngIf="depth>0" (click)="removeSelf(g)">Remove</button>
    </div>

    <div *ngFor="let r of g.rules; let i=index" class="wcg-filter-rule">
      <select class="wcg__select" [(ngModel)]="r.field" (change)="onFieldChanged(r)">
        <option value="" disabled>— Select field —</option>
        <option *ngFor="let c of filterableColumns" [value]="c.field">{{c.header}}</option>
      </select>

      <select class="wcg__select" [(ngModel)]="r.op" (change)="onOpChanged(r)">
        <option *ngFor="let op of opsForRule(r)"
        [value]="op"
        [title]="FILTER_OP_LABELS[op]">
  {{ FILTER_OP_LABELS[op] }}
</option>
      </select>

      <ng-container *ngIf="needsValue(r.op)">

        <!-- ── IN operator ──────────────────────────────────────────── -->
        <div *ngIf="r.op === 'In'" class="wcg-in-editor">
          <div class="wcg-in-row" *ngFor="let item of (r.value ?? []); let idx = index">

            <!-- Lookup In: dropdown per item -->
            <ng-container *ngIf="col(r.field)?.lookupKey; else plainInInput">
              <select class="wcg__select wcg__select--sm"
                      [(ngModel)]="r.value[idx]"
                      (change)="emit()">
                <option [ngValue]="null">--</option>
                <option *ngFor="let o of lookupOpts(r.field)" [ngValue]="o.value">{{o.label}}</option>
              </select>
            </ng-container>

            <!-- Plain In: type-based inputs -->
            <ng-template #plainInInput>
              <ng-container [ngSwitch]="kind(r.field)">
                <input *ngSwitchCase="'number'"
                       type="number"
                       class="wcg__input wcg__input--sm"
                       [(ngModel)]="r.value[idx]"
                       (keydown.enter)="applyFilters()"
                       placeholder="value" />
                <input *ngSwitchCase="'date'"
                       type="date"
                       class="wcg__input wcg__input--sm"
                       [(ngModel)]="r.value[idx]"
                       (change)="emit()" />
                <input *ngSwitchCase="'datetime'"
                       type="datetime-local"
                       class="wcg__input wcg__input--sm"
                       [(ngModel)]="r.value[idx]"
                       (change)="emit()" />
                <select *ngSwitchCase="'bool'"
                        class="wcg__select"
                        [(ngModel)]="r.value[idx]"
                        (change)="emit()">
                  <option [ngValue]="true">True</option>
                  <option [ngValue]="false">False</option>
                </select>
                <input *ngSwitchDefault
                       class="wcg__input wcg__input--sm"
                       [(ngModel)]="r.value[idx]"
                       (keydown.enter)="applyFilters()"
                       placeholder="value" />
              </ng-container>
            </ng-template>

            <button class="wcg-btn wcg-btn--danger wcg-btn--xs"
                    (click)="removeInItem(r, idx)">x</button>
          </div>

          <button class="wcg-btn wcg-btn--ghost wcg-btn--xs"
                  (click)="addInItem(r)">+ Add</button>
        </div>

        <!-- ── Single-value operators ───────────────────────────────── -->
        <ng-container *ngIf="r.op !== 'In'">

          <!-- Lookup: single dropdown -->
          <ng-container *ngIf="col(r.field)?.lookupKey; else kindInput">
            <select class="wcg__select"
                    [(ngModel)]="r.value"
                    (change)="emit()">
              <option [ngValue]="null">--</option>
              <option *ngFor="let o of lookupOpts(r.field)" [ngValue]="o.value">{{o.label}}</option>
            </select>
          </ng-container>

          <!-- Non-lookup: type-based input -->
          <ng-template #kindInput>
            <ng-container [ngSwitch]="kind(r.field)">
              <input *ngSwitchCase="'number'"
                     type="number"
                     class="wcg__input wcg__input--sm"
                     [(ngModel)]="r.value"
                     (keydown.enter)="applyFilters()"
                     placeholder="0" />
              <input *ngSwitchCase="'date'"
                     type="date"
                     class="wcg__input wcg__input--sm"
                     [(ngModel)]="r.value"
                     (change)="emit()" />
              <input *ngSwitchCase="'datetime'"
                     type="datetime-local"
                     class="wcg__input wcg__input--sm"
                     [(ngModel)]="r.value"
                     (change)="emit()" />
              <select *ngSwitchCase="'bool'"
                      class="wcg__select"
                      [(ngModel)]="r.value"
                      (change)="emit()">
                <option [ngValue]="true">True</option>
                <option [ngValue]="false">False</option>
              </select>
              <input *ngSwitchDefault
                     class="wcg__input wcg__input--sm"
                     [(ngModel)]="r.value"
                     (keydown.enter)="applyFilters()"
                     [placeholder]="'value'" />
            </ng-container>
          </ng-template>

        </ng-container>
      </ng-container>

      <button class="wcg-btn wcg-btn--danger wcg-btn--xs" (click)="removeRule(g, i)">x</button>
    </div>

    <div *ngFor="let cg of (g.groups ?? [])">
      <ng-container *ngTemplateOutlet="groupTpl; context:{ g: cg, depth: depth + 1 }"></ng-container>
    </div>
  </div>
</ng-template>

  `
})
export class WcFilterBuilderComponent implements OnChanges {
  @Input() columns: GridColumnDef[] = [];
  @Input() model: FilterGroup = { logic: 'AND', rules: [], groups: [] };
  @Input() gridKey = '';
  @Input() dashboardId = '';
  @Output() modelChange = new EventEmitter<FilterGroup>();
  @Output() apply = new EventEmitter<FilterGroup>();

  FILTER_OP_LABELS = FILTER_OP_LABELS;
  ops: FilterOp[] = ['Eq','Neq','Contains','StartsWith','Gt','Gte','Lt','Lte','IsNull','IsNotNull','In'];

  private lookupCache = new Map<string, LookupOption[]>();

  constructor(private api: GridApiService) {}

  ngOnChanges(changes: SimpleChanges) {
    // When model or columns arrive/change, pre-load lookups for any existing rules
    if (changes['model'] || changes['columns']) {
      this.preloadLookups(this.model);
    }
  }

  private preloadLookups(g: FilterGroup) {
    for (const r of g.rules ?? []) {
      const lookupKey = this.col(r.field)?.lookupKey;
      if (lookupKey) this.loadLookup(lookupKey);
    }
    for (const sg of g.groups ?? []) {
      this.preloadLookups(sg);
    }
  }

  /** Returns cached lookup options, triggering a fetch on first access. */
  lookupOpts(field: string): LookupOption[] {
    const lookupKey = this.col(field)?.lookupKey;
    if (!lookupKey) return [];
    if (!this.lookupCache.has(lookupKey)) this.loadLookup(lookupKey);
    return this.lookupCache.get(lookupKey) ?? [];
  }

  private loadLookup(lookupKey: string) {
    if (this.lookupCache.has(lookupKey)) return;
    if (!this.gridKey && !this.dashboardId) return;
    this.lookupCache.set(lookupKey, []); // sentinel to prevent duplicate requests
    const cb = (opts: LookupOption[]) => this.lookupCache.set(lookupKey, opts);
    if (this.dashboardId) {
      this.api.dashboardLookup(this.dashboardId, lookupKey, { take: 500 }, cb);
    } else {
      this.api.lookup(this.gridKey, lookupKey, { take: 500 }, cb);
    }
  }

  get filterableColumns() {
    return this.columns.filter(c => c.filterable);
  }

  addRule(g: FilterGroup) {
    const first = this.filterableColumns[0]?.field ?? this.columns[0]?.field ?? '';
    const k = this.kind(first);
    const op = OPS_BY_KIND[k][0];
    const value =
      k === 'bool' ? true :
        k === 'number' ? 0 :
          '';

    g.rules.push({ field: first, op, value });

    const lookupKey = this.col(first)?.lookupKey;
    if (lookupKey) this.loadLookup(lookupKey);

    this.emit();
  }

  addGroup(g: FilterGroup) {
    g.groups = g.groups ?? [];
    g.groups.push({ logic: 'AND', rules: [], groups: [] });
    this.emit();
  }

  removeRule(g: FilterGroup, i: number) {
    g.rules.splice(i, 1);
    this.emit();
  }

  removeSelf(target: FilterGroup) {
    const removed = this.removeGroupRecursive(this.model, target);
    if (removed) this.emit();
  }

  private removeGroupRecursive(parent: FilterGroup, target: FilterGroup): boolean {
    if (!parent.groups) return false;
    const idx = parent.groups.indexOf(target);
    if (idx >= 0) { parent.groups.splice(idx, 1); return true; }
    for (const g of parent.groups) {
      if (this.removeGroupRecursive(g, target)) return true;
    }
    return false;
  }

  clear() {
    this.model.rules = [];
    this.model.groups = [];
    this.model.logic = 'AND';
    this.applyFilters();
  }

  col(field?: string): GridColumnDef | undefined {
    return this.columns.find(c => c.field === field);
  }

  kind(field?: string): GridFilterKind {
    const dt = this.col(field)?.dataType ?? 'String';
    return KIND_BY_DATATYPE[dt];
  }

  opsForRule(r: FilterRule): FilterOp[] {
    const c = this.col(r.field);
    if (c?.lookupKey) return ['Eq', 'Neq', 'In', 'IsNull', 'IsNotNull'];
    return OPS_BY_KIND[this.kind(r.field)];
  }

  needsValue(op: FilterOp) {
    return op !== 'IsNull' && op !== 'IsNotNull';
  }

  onFieldChanged(r: any) {
    const allowed = this.opsForRule(r);
    if (!allowed.includes(r.op)) r.op = allowed[0];
    r.value = (r.op === 'In') ? [null] : null;
    r.value2 = null;

    const lookupKey = this.col(r.field)?.lookupKey;
    if (lookupKey) this.loadLookup(lookupKey);

    this.emit();
  }

  onOpChanged(r: any) {
    if (r.op === 'In') {
      this.ensureInArray(r);
    } else if (!this.needsValue(r.op)) {
      r.value = null;
      r.value2 = null;
    } else if (Array.isArray(r.value)) {
      r.value = null;
    }
    this.emit();
  }

  private normalizeRuleValue(r: FilterRule): any {
    if (!this.needsValue(r.op)) return undefined;

    const isLookup = !!this.col(r.field)?.lookupKey;
    const k = this.kind(r.field);
    const raw = r.value;

    if (r.op === 'In') {
      const parts: any[] = Array.isArray(raw) ? raw.filter(v => v != null && v !== '') : [];

      if (isLookup) return parts.length ? parts : undefined;

      if (k === 'number') {
        const nums = parts.map(x => Number(x)).filter(n => Number.isFinite(n));
        return nums.length ? nums : undefined;
      }
      if (k === 'bool') return parts.map(x => x === true || x === 'true');
      return parts.length ? parts : undefined;
    }

    if (isLookup) {
      // Lookup values are already strings (the option's value property)
      return raw != null && raw !== '' ? String(raw) : undefined;
    }

    switch (k) {
      case 'number': {
        const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
        return Number.isFinite(n) ? n : undefined;
      }
      case 'bool': {
        if (raw === true || raw === false) return raw;
        const s = String(raw ?? '').toLowerCase();
        if (s === 'true' || s === '1') return true;
        if (s === 'false' || s === '0') return false;
        return undefined;
      }
      case 'date': {
        const s = String(raw ?? '').trim();
        return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : undefined;
      }
      case 'string':
      default:
        return String(raw ?? '').trim() || undefined;
    }
  }

  /** Sync model to parent (two-way binding) without triggering a re-query. */
  emit() {
    this.modelChange.emit(this.model);
  }

  /** Normalize values and fire the apply event to trigger a re-query. */
  applyFilters() {
    const normalizeGroup = (g: FilterGroup) => {
      for (const r of g.rules) {
        r.value = this.normalizeRuleValue(r);
      }
      for (const cg of (g.groups ?? [])) normalizeGroup(cg);
    };
    normalizeGroup(this.model);

    this.modelChange.emit(this.model);
    this.apply.emit(this.model);
  }

  isIn(op: FilterOp) { return op === 'In'; }

  ensureInArray(r: any) {
    if (!Array.isArray(r.value)) r.value = [null];
    if (r.value.length === 0) r.value = [null];
  }

  addInItem(r: any) {
    this.ensureInArray(r);
    r.value.push(null);
    this.emit();
  }

  removeInItem(r: any, idx: number) {
    this.ensureInArray(r);
    r.value.splice(idx, 1);
    if (r.value.length === 0) r.value.push(null);
    this.emit();
  }
}
